import { defineStore } from 'pinia';
import { httpAPI } from '@/services/ajax';
import app from '@/main.js';
import { usePermissionStore } from './permission';
import { normalizeSubscriptionDetails, SUBSCRIPTION_STATUS } from '@/utils/subscriptionStatus';

export const useUserStore = defineStore('user', {
    state: () => ({
        user: null,
        isAuthenticated: false,
        company: null,
        isPremiumModalVisible: false,
        isPermissionDeniedModalVisible: false,
        isComingSoonModalVisible: false,

        fetching: false, // are we currently fetching the user?
        callbackBus: {}, // functions to call once fetch is done
    }),
    actions: {
        _fetchUserAndTriggerCallbacks() {
            /** Force fetch the user from the API, then perform all the callbacks in callbackBus */
            if (!app) return;

            this.fetching = true;

            const vm = this;
            const url = app.config.globalProperties.$env.apiRoot + 'user/me';
            const payload = {
                include: {
                    company: {
                        include: {
                            sector: true,
                        },
                    },
                    sites: true,
                },
            };

            httpAPI(url, payload, 'GET', app.config.globalProperties.$router)
                .then((response) => {
                    if ('success' in response && response.success) {
                        const normalizedUser = {
                            ...response.data,
                            subscription_details: normalizeSubscriptionDetails(
                                response?.data?.subscription_details ?? {}
                            ),
                        };
                        Object.freeze(normalizedUser);

                        vm.user = normalizedUser;
                        vm.company = normalizedUser.company;
                        vm.isAuthenticated = true;
                    } else {
                        //const alertStore = useAlertStore();
                        //alertStore.pushError("Failed to fetch data for user store: " + response.message)
                    }
                    this.fetching = false;
                })
                .catch(() => {
                    this.fetching = false;
                })
                .finally(() => {
                    this.fetching = false;

                    // Perform list of callback functions
                    for (const callbackId in this.callbackBus) {
                        const callback = this.callbackBus[callbackId];
                        try {
                            callback(this.user);
                        } catch (error) {
                            console.error(
                                'Error in user store callback',
                                error
                            );
                        }
                        delete this.callbackBus[callbackId]; // Remove it from bus
                    }
                });
        },

        fetchUserData(callback = null) {
            /** Fetch user from API and perform the given callback once completed */

            // Add callback to our bus
            if (callback) {
                const callbackIds = Object.keys(this.callbackBus);
                const lastId = callbackIds.length
                    ? callbackIds[callbackIds.length - 1]
                    : 0;
                this.callbackBus[lastId + 1] = callback;
            }

            // Only start resyncing if not already resyncing
            if (!this.fetching) {
                this._fetchUserAndTriggerCallbacks();
            }
        },

        sync(callback = null) {
            /** Fetch the user from the API (if necessary), then perform the callback function once completed */
            if (!this.user || (this.user.company_id && !this.company))
                this.fetchUserData(callback);
            else if (callback) {
                callback(this.user);
            }
        },

        clear() {
            this.user = null;
            this.company = null;
            this.isAuthenticated = false;
            this.fetching = false;
            this.callbackBus = {};
        },

        companyHasPerm(module) {
            const permStore = usePermissionStore();
            return permStore.companyHasPerm(module);
        },

        companyHasPermOneOf(...modules) {
            const permStore = usePermissionStore();
            return permStore.companyHasPermOneOf(...modules);
        },

        hasPerm(module) {
            const permStore = usePermissionStore();
            return permStore.hasPerm(module);
        },

        hasPermOneOf(...modules) {
            const permStore = usePermissionStore();
            return permStore.hasPermOneOf(...modules);
        },

        togglePremiumModal(val) {
            this.isPremiumModalVisible = val;
        },

        togglePermissionDeniedModal(val) {
            this.isPermissionDeniedModalVisible = val;
        },

        toggleComingSoonModal(val) {
            this.isComingSoonModalVisible = val;
        },
    },

    getters: {
        getFullName(state) {
            return state.user
                ? ['first_name', 'last_name']
                      .map((key) => state.user[key])
                      .join(' ')
                : 'N/A';
        },

        isCompanyAdmin(state) {
            return (
                state.isCompanyOwner || Boolean(state.user?.is_company_admin)
            );
        },

        isCompanyOwner(state) {
            return (
                state.isAppAdmin ||
                Boolean(
                    state.user?.id && state.user?.id == state.company?.admin_id
                )
            );
        },

        isAppAdmin(state) {
            return Boolean(state.user?.is_app_admin);
        },


        hasAlphaAccess(state) {
            return Boolean(state.user?.company?.override_permissions_json?.includes('alpha:access'));
        },

        subscriptionDetails(state) {
            return state.user?.subscription_details ?? null;
        },

        subscriptionStatus(state) {
            return state.user?.subscription_details?.status ?? null;
        },

        isSubscriptionActive(state) {
            return Boolean(state.user?.subscription_details?.hasActivePlan);
        },

        isViewOnly(state) {
            return state.user?.subscription_details?.status === SUBSCRIPTION_STATUS.VIEW_ONLY;
        },
    },
});
